import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Gift, 
  History, 
  Star, 
  Phone, 
  QrCode,
  ArrowRight,
  CheckCircle2,
  Clock,
  Loader2,
  ShoppingBag,
  Award,
  TrendingUp,
  Lock,
  Bell,
  X,
  Megaphone,
  Sparkles,
  Tag,
  PartyPopper,
  Download,
  Smartphone,
  Share,
  Plus,
  ChevronRight,
  LogOut,
  UtensilsCrossed
} from 'lucide-react';
import QRCode from 'qrcode';
import SmartFrogMascot from '../components/FrogMascot';

// MR FROGS Color Palette
const COLORS = {
  primary: '#7CB342',      // Frog green
  primaryDark: '#558B2F',  // Darker green
  gold: '#C5A028',         // Gold/Yellow
  goldLight: '#D4AF37',    // Light gold
  navy: '#1A237E',         // Dark blue
  navyLight: '#283593',    // Lighter navy
  water: '#81D4FA',        // Water blue
  white: '#FFFFFF',
  cream: '#F5F5DC'
};

const CustomerPortal = () => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null); // Dynamic restaurant ID
  const [redeemableProducts, setRedeemableProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('card'); // card, redeem, history
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [pendingRedemption, setPendingRedemption] = useState(null);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [autoLoading, setAutoLoading] = useState(true); // Para carga automática
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationSoundPlayed, setNotificationSoundPlayed] = useState(false);
  
  // Function to play notification sound
  const playNotificationSound = () => {
    try {
      // Create a pleasant "ding-ding" sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Resume audio context (required for some browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      // First ding
      const playDing = (freq, delay, volume) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + delay);
        oscillator.type = 'sine';
        
        // Louder and clearer envelope
        gainNode.gain.setValueAtTime(0, audioContext.currentTime + delay);
        gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + delay + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + delay + 0.6);
        
        oscillator.start(audioContext.currentTime + delay);
        oscillator.stop(audioContext.currentTime + delay + 0.6);
      };
      
      // Play two dings for "ding-ding" effect
      playDing(880, 0, 0.5);      // First ding (A5) - louder
      playDing(1100, 0.2, 0.4);   // Second ding (higher) 
      playDing(1320, 0.4, 0.3);   // Third ding (even higher)
      
      console.log('🔔 Notification sound played');
      
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  };
  
  // PWA Install state
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  
  // In production, use same origin. In development, use env variable
  const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const [showWelcome, setShowWelcome] = useState(false);
  const [animateCard, setAnimateCard] = useState(false);

  // Helper function to get full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    // Debug: Log the URL transformation
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    let finalUrl = '';
    
    // If URL starts with /api/, it's a relative API path - use current origin in production
    if (url.startsWith('/api/')) {
      finalUrl = `${baseUrl}${url}`;
    }
    // If URL is already absolute, return as is
    else if (url.startsWith('http://') || url.startsWith('https://')) {
      finalUrl = url;
    }
    // If URL is relative, prepend base URL
    else {
      finalUrl = `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
    }
    
    console.log('[CustomerPortal] Image URL:', { original: url, baseUrl, final: finalUrl });
    return finalUrl;
  };

  // Auto-login: Cargar teléfono guardado al iniciar
  useEffect(() => {
    const savedPhone = localStorage.getItem('mrfrogs_vip_phone');
    if (savedPhone) {
      setPhone(savedPhone);
      // Auto-cargar datos del cliente
      autoLoadCustomer(savedPhone);
    } else {
      setAutoLoading(false);
    }
  }, []);

  // Función para cargar cliente automáticamente
  const autoLoadCustomer = async (savedPhone) => {
    try {
      const response = await fetch(`${API_URL}/api/customer-portal/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: savedPhone })
      });

      const data = await response.json();
      
      if (response.ok && data.customer) {
        setCustomerData(data);
        setRestaurantId(data.restaurant_id);
        loadRedeemableProducts(data.restaurant_id);
      } else {
        // Si falla, borrar teléfono guardado
        localStorage.removeItem('mrfrogs_vip_phone');
      }
    } catch (error) {
      console.error('Auto-login error:', error);
      localStorage.removeItem('mrfrogs_vip_phone');
    } finally {
      setAutoLoading(false);
    }
  };

  // Check if PWA is already installed and detect iOS
  useEffect(() => {
    // Simple iOS detection
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent || '');
    setIsIOS(isIOSDevice);
    
    // Check if already installed as PWA
    if (window.navigator.standalone === true) {
      setIsAppInstalled(true);
    }
    
    // Android install prompt
    if (!isIOSDevice) {
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        window.deferredPrompt = e;
        if (customerData && !isAppInstalled) {
          setTimeout(() => setShowInstallBanner(true), 3000);
        }
      };
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
      return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    }
  }, [customerData, isAppInstalled]);

  // Show install banner when customer data loads
  useEffect(() => {
    if (customerData && !isAppInstalled) {
      // Show banner after 3 seconds
      setTimeout(() => setShowInstallBanner(true), 3000);
    }
  }, [customerData, isAppInstalled]);

  // Handle PWA install - simplified
  const handleInstallClick = () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      setShowInstallBanner(false);
      return;
    }
    
    const promptEvent = window.deferredPrompt;
    if (promptEvent) {
      promptEvent.prompt();
      promptEvent.userChoice.then((result) => {
        if (result.outcome === 'accepted') {
          toast.success('¡App instalada!');
          setIsAppInstalled(true);
        }
        setShowInstallBanner(false);
        window.deferredPrompt = null;
      });
    } else {
      toast.info('Usa el menú del navegador para instalar');
      setShowInstallBanner(false);
    }
  };

  // Generate QR Code when customer data loads
  useEffect(() => {
    if (customerData?.qr_data) {
      QRCode.toDataURL(customerData.qr_data, {
        width: 200,
        margin: 2,
        color: {
          dark: COLORS.navy,
          light: COLORS.white
        }
      }).then(url => setQrCodeUrl(url));
      
      // Trigger animations
      setTimeout(() => setShowWelcome(true), 100);
      setTimeout(() => setAnimateCard(true), 400);
    }
  }, [customerData]);

  const lookupCustomer = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Ingresa un número de teléfono válido (10 dígitos)');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer-portal/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })  // No need to send restaurant_id - backend finds it
      });

      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Cliente no encontrado');
        return;
      }

      // Guardar teléfono para auto-login futuro
      localStorage.setItem('mrfrogs_vip_phone', phone);
      
      setCustomerData(data);
      setRestaurantId(data.restaurant_id);  // Save restaurant_id from response
      toast.success(`¡Bienvenido ${data.customer.name}!`);
      
      // Log portal visit
      logPortalVisit(data.restaurant_id, 'card');
      
      // Load redeemable products with the restaurant_id
      loadRedeemableProducts(data.restaurant_id);
      
      // Play notification sound if there are notifications (user has already interacted)
      setTimeout(() => {
        playNotificationSound();
      }, 1500);
    } catch (error) {
      toast.error('Error al buscar tu cuenta');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Log portal visit for tracking
  const logPortalVisit = async (restId, section) => {
    try {
      await fetch(`${API_URL}/api/customer-portal/log-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone,
          restaurant_id: restId,
          section: section
        })
      });
    } catch (error) {
      console.error('Error logging visit:', error);
    }
  };

  const loadRedeemableProducts = async (restId) => {
    const rId = restId || restaurantId;
    if (!rId) return;
    try {
      const response = await fetch(
        `${API_URL}/api/customer-portal/redeemable-products/${rId}?phone=${phone}`
      );
      const data = await response.json();
      setRedeemableProducts(data.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  // Load notifications
  const loadNotifications = async (restId, playSound = false) => {
    const rId = restId || restaurantId;
    if (!rId) return;
    try {
      const response = await fetch(
        `${API_URL}/api/customer-portal/notifications/${rId}?phone=${phone}`
      );
      const data = await response.json();
      setNotifications(data.notifications || []);
      const newUnreadCount = data.unread_count || 0;
      setUnreadCount(newUnreadCount);
      
      // Play sound if there are unread notifications and sound hasn't been played yet
      if (playSound && newUnreadCount > 0 && !notificationSoundPlayed) {
        playNotificationSound();
        setNotificationSoundPlayed(true);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    if (!restaurantId) return;
    try {
      await fetch(`${API_URL}/api/customer-portal/notifications/mark-read?notification_id=${notificationId}&phone=${phone}&restaurant_id=${restaurantId}`, {
        method: 'POST'
      });
      loadNotifications();
    } catch (error) {
      console.error('Error marking notification:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    if (!restaurantId) return;
    try {
      await fetch(`${API_URL}/api/customer-portal/notifications/mark-all-read?phone=${phone}&restaurant_id=${restaurantId}`, {
        method: 'POST'
      });
      setUnreadCount(0);
      loadNotifications();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Load notifications when customer data loads (with sound on first load)
  useEffect(() => {
    if (customerData && phone && restaurantId) {
      loadNotifications(restaurantId, true); // true = play sound if there are notifications
    }
  }, [customerData, phone, restaurantId]);

  // AUTO-REFRESH: Update all data every 10 seconds for real-time experience
  useEffect(() => {
    if (!customerData || !phone || !restaurantId) return;
    
    const refreshAllData = async () => {
      try {
        // Refresh customer data (points, visits, etc.)
        const customerResponse = await fetch(`${API_URL}/api/customer-portal/lookup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone })
        });
        if (customerResponse.ok) {
          const data = await customerResponse.json();
          if (data.customer) {
            setCustomerData(data);
          }
        }
        
        // Refresh notifications (without sound on auto-refresh)
        loadNotifications(restaurantId, false);
        
        // Refresh redeemable products
        loadRedeemableProducts(restaurantId);
        
      } catch (error) {
        console.log('Auto-refresh error:', error);
      }
    };
    
    // Set up interval for auto-refresh every 10 seconds
    const intervalId = setInterval(refreshAllData, 10000);
    
    // Cleanup on unmount
    return () => clearInterval(intervalId);
  }, [customerData, phone, restaurantId, API_URL]);

  // Get notification icon by type
  const getNotificationIcon = (type) => {
    switch(type) {
      case 'promotion': return <Tag className="w-5 h-5" />;
      case 'points': return <Sparkles className="w-5 h-5" />;
      case 'birthday': return <PartyPopper className="w-5 h-5" />;
      case 'reminder': return <Bell className="w-5 h-5" />;
      default: return <Megaphone className="w-5 h-5" />;
    }
  };

  // Get notification color by type
  const getNotificationColor = (type) => {
    switch(type) {
      case 'promotion': return { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200' };
      case 'points': return { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' };
      case 'birthday': return { bg: 'bg-pink-100', text: 'text-pink-600', border: 'border-pink-200' };
      case 'reminder': return { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' };
      default: return { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' };
    }
  };

  const requestRedemption = async (productId) => {
    if (!restaurantId) {
      toast.error('Error: No se encontró el restaurante');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer-portal/request-redemption`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          restaurant_id: restaurantId,
          product_id: productId
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Error al solicitar canje');
        return;
      }

      setPendingRedemption(data);
      setShowRedemptionModal(true);
      toast.success('¡Canje solicitado! Muestra el código al cajero.');
    } catch (error) {
      toast.error('Error al procesar el canje');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tier) => {
    switch(tier) {
      case 'diamante': return '💎';
      case 'oro': return '🥇';
      case 'plata': return '🥈';
      default: return '🥉';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Pantalla de carga mientras hace auto-login
  if (autoLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ 
          background: `linear-gradient(135deg, ${COLORS.navy} 0%, #0d1442 50%, ${COLORS.navyLight} 100%)`
        }}
      >
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden border-4 border-amber-400 shadow-lg">
            <img 
              src="https://scontent.fhmo4-1.fna.fbcdn.net/v/t39.30808-6/461427158_122130963096351389_5765570663064819270_n.jpg"
              alt="Mr Frogs"
              className="w-full h-full object-cover"
            />
          </div>
          <Loader2 className="w-8 h-8 text-white animate-spin mx-auto mb-2" />
          <p className="text-white/80 text-sm">Cargando tu tarjeta...</p>
        </div>
      </div>
    );
  }

  // Login Screen - Professional & Dynamic Design
  if (!customerData) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Header de navegación */}
        <header className="sticky top-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-white/10 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <span className="text-2xl">🐸</span>
              <h1 className="text-lg font-bold text-white tracking-tight">Mr. Frogs</h1>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-2">
              {/* Menú Button */}
              <a
                href="/menu"
                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-full text-sm font-medium transition-all shadow-lg shadow-cyan-500/20"
              >
                <UtensilsCrossed className="w-4 h-4" />
                <span>Menú</span>
              </a>
              
              {/* VIP Button - Active */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm font-medium shadow-lg shadow-amber-500/20">
                <Star className="w-4 h-4" />
                <span className="hidden sm:inline">VIP</span>
              </div>
            </div>
          </div>
        </header>

        {/* Animated Background */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: `linear-gradient(135deg, ${COLORS.navy} 0%, #0d1442 50%, ${COLORS.navyLight} 100%)`
          }}
        />
        
        {/* Floating Bubbles Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full opacity-10"
              style={{
                width: `${60 + i * 30}px`,
                height: `${60 + i * 30}px`,
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.water})`,
                left: `${10 + i * 15}%`,
                bottom: `-${50 + i * 20}px`,
                animation: `floatUp ${8 + i * 2}s ease-in-out infinite`,
                animationDelay: `${i * 1.5}s`
              }}
            />
          ))}
        </div>

        {/* Glow Effect */}
        <div 
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ background: COLORS.primary }}
        />

        {/* Content */}
        <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            {/* Logo Section with Glow */}
            <div className="text-center mb-8 animate-fadeIn">
              <div className="relative inline-block">
                {/* Glow ring */}
                <div 
                  className="absolute inset-0 rounded-full blur-xl opacity-50 animate-pulse"
                  style={{ 
                    background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.primary})`,
                    transform: 'scale(1.2)'
                  }}
                />
                <img 
                  src="https://customer-assets-m6fa6gv7.emergentagent.net/job_84eb74f9-05f0-495f-9ed7-6adf8a564e0e/artifacts/jk85sh7d_LOGO%20FROGS.jpg"
                  alt="Mr Frogs Logo"
                  className="relative w-40 h-40 rounded-full shadow-2xl object-cover border-4 transition-transform duration-500 hover:scale-105"
                  style={{ borderColor: COLORS.gold }}
                />
              </div>
              
              <h1 
                className="text-4xl font-black mt-6 tracking-wide"
                style={{ 
                  color: COLORS.gold,
                  textShadow: `0 0 30px ${COLORS.gold}40`
                }}
              >
                MR FROGS
              </h1>
              <div className="flex items-center justify-center gap-2 mt-2">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500/50" />
                <p className="text-white/70 text-sm font-medium tracking-widest uppercase">
                  Club de Lealtad VIP
                </p>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500/50" />
              </div>
            </div>

            {/* Card with Glass Effect */}
            <Card 
              className="border-0 shadow-2xl backdrop-blur-xl animate-slideUp"
              style={{ 
                background: 'rgba(255,255,255,0.95)',
                borderRadius: '24px'
              }}
            >
              <CardContent className="p-8">
                {/* Welcome Text */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 text-green-700 text-sm font-medium mb-4">
                    <Star className="w-4 h-4" />
                    Acumula puntos y gana premios
                  </div>
                  <p className="text-gray-600">
                    Ingresa tu número de teléfono para acceder a tu tarjeta VIP
                  </p>
                </div>
                
                {/* Phone Input with Animation */}
                <div className="relative group mb-6">
                  <div 
                    className="absolute -inset-1 rounded-2xl opacity-0 group-focus-within:opacity-100 blur transition-all duration-300"
                    style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.gold})` }}
                  />
                  <div className="relative flex items-center bg-gray-50 rounded-xl border-2 border-transparent focus-within:border-green-400 focus-within:bg-white transition-all duration-300">
                    <div className="pl-4 pr-2">
                      <Phone className="w-5 h-5 text-gray-400" />
                    </div>
                    <Input
                      type="tel"
                      placeholder="10 dígitos"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="border-0 bg-transparent text-xl h-16 text-center tracking-[0.3em] font-semibold focus:ring-0 focus-visible:ring-0 placeholder:tracking-normal placeholder:font-normal"
                      maxLength={10}
                    />
                  </div>
                </div>

                {/* Progress Dots */}
                <div className="flex justify-center gap-2 mb-6">
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        i < phone.length 
                          ? 'bg-green-500 scale-110' 
                          : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>

                {/* Submit Button */}
                <Button 
                  onClick={lookupCustomer}
                  disabled={loading || phone.length < 10}
                  className="w-full h-14 text-lg font-bold rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
                  style={{ 
                    background: phone.length >= 10 
                      ? `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`
                      : '#e5e7eb',
                    color: phone.length >= 10 ? COLORS.white : '#9ca3af',
                    boxShadow: phone.length >= 10 
                      ? `0 10px 30px -10px ${COLORS.primary}80`
                      : 'none'
                  }}
                >
                  {loading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Ver Mi Tarjeta VIP
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                {/* Benefits Preview */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <p className="text-center text-sm text-gray-500 mb-4">Beneficios exclusivos</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 rounded-xl bg-gradient-to-br from-green-50 to-green-100">
                      <Gift className="w-6 h-6 mx-auto mb-1 text-green-600" />
                      <p className="text-xs text-green-700 font-medium">Premios Gratis</p>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100">
                      <Award className="w-6 h-6 mx-auto mb-1 text-amber-600" />
                      <p className="text-xs text-amber-700 font-medium">Niveles VIP</p>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100">
                      <TrendingUp className="w-6 h-6 mx-auto mb-1 text-blue-600" />
                      <p className="text-xs text-blue-700 font-medium">Descuentos</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Footer */}
            <p className="text-center text-white/40 text-xs mt-6">
              ¿No tienes cuenta? Regístrate en tu próxima visita
            </p>
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes floatUp {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            50% { transform: translateY(-400px) rotate(180deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes wiggle {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(-10deg); }
            75% { transform: rotate(10deg); }
          }
          .animate-fadeIn { animation: fadeIn 0.8s ease-out forwards; }
          .animate-slideUp { animation: slideUp 0.6s ease-out forwards; animation-delay: 0.2s; opacity: 0; }
          .animate-slideInRight { animation: slideInRight 0.4s ease-out forwards; }
          .animate-wiggle { animation: wiggle 0.5s ease-in-out infinite; }
        `}</style>
      </div>
    );
  }

  // Main Portal with Card
  return (
    <div 
      className="min-h-screen pb-20 relative"
      style={{ 
        background: `linear-gradient(180deg, ${COLORS.navy} 0%, ${COLORS.navyLight} 25%, #ffffff 25%)`
      }}
    >
      {/* Header de navegación */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-md border-b border-white/10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <span className="text-2xl">🐸</span>
            <h1 className="text-lg font-bold text-white tracking-tight">Mr. Frogs</h1>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            {/* Menú Button */}
            <a
              href="/menu"
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-full text-sm font-medium transition-all shadow-lg shadow-cyan-500/20"
            >
              <UtensilsCrossed className="w-4 h-4" />
              <span>Menú</span>
            </a>
            
            {/* VIP Button - Active */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm font-medium shadow-lg shadow-amber-500/20">
              <Star className="w-4 h-4" />
              <span className="hidden sm:inline">VIP</span>
            </div>
            
            {/* Exit Button */}
            <button
              onClick={() => {
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  window.location.href = '/menu';
                }
              }}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white rounded-full text-sm font-medium transition-all border border-white/10"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      {/* Notifications Panel Overlay */}
      {showNotifications && (
        <div 
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn"
          onClick={() => setShowNotifications(false)}
        >
          <div 
            className="absolute top-0 right-0 w-full max-w-md h-full bg-white shadow-2xl animate-slideInRight"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Notifications Header */}
            <div 
              className="p-4 flex items-center justify-between sticky top-0 z-10"
              style={{ background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.navyLight})` }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Notificaciones</h2>
                  <p className="text-xs text-white/70">{unreadCount} sin leer</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                    className="text-white/70 hover:text-white hover:bg-white/20 text-xs"
                  >
                    Marcar todo leído
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowNotifications(false)}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="p-4 space-y-3 overflow-y-auto h-[calc(100%-80px)]">
              {notifications.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Bell className="w-10 h-10 text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-medium">No tienes notificaciones</p>
                  <p className="text-gray-400 text-sm mt-1">Te avisaremos cuando haya novedades</p>
                </div>
              ) : (
                notifications.map((notif, index) => {
                  const colors = getNotificationColor(notif.notification_type);
                  return (
                    <div
                      key={notif.notification_id}
                      className={`p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer hover:shadow-lg hover:scale-[1.02] ${
                        notif.is_read ? 'bg-gray-50 border-gray-100' : `${colors.bg} ${colors.border}`
                      }`}
                      style={{
                        animation: `slideInRight 0.3s ease-out forwards`,
                        animationDelay: `${index * 0.1}s`,
                        opacity: 0
                      }}
                      onClick={() => !notif.is_read && markAsRead(notif.notification_id)}
                    >
                      <div className="flex gap-3">
                        <div className={`w-10 h-10 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center flex-shrink-0`}>
                          {getNotificationIcon(notif.notification_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className={`font-bold ${notif.is_read ? 'text-gray-600' : 'text-gray-800'}`}>
                              {notif.title}
                            </h3>
                            {!notif.is_read && (
                              <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 mt-2 animate-pulse" />
                            )}
                          </div>
                          <p className={`text-sm mt-1 ${notif.is_read ? 'text-gray-400' : 'text-gray-600'}`}>
                            {notif.message}
                          </p>
                          {notif.image_url && (
                            <div className="mt-3 rounded-xl overflow-hidden">
                              <img 
                                src={getImageUrl(notif.image_url)} 
                                alt="" 
                                className="w-full h-36 object-cover"
                              />
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(notif.created_at).toLocaleDateString('es-MX', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="p-4 pt-6">
        <div className="flex justify-between items-center text-white mb-4">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_84eb74f9-05f0-495f-9ed7-6adf8a564e0e/artifacts/jk85sh7d_LOGO%20FROGS.jpg"
              alt="Mr Frogs Logo"
              className="w-12 h-12 rounded-full shadow-md object-cover border-2"
              style={{ borderColor: COLORS.gold }}
            />
            <div>
              <h1 className="text-xl font-bold" style={{ color: COLORS.gold }}>MR FROGS</h1>
              <p className="text-sm opacity-80">Club VIP</p>
            </div>
          </div>
          
          {/* Notification Bell & Exit */}
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowNotifications(true)}
              className="relative text-white hover:bg-white/20 transition-all duration-300"
            >
              <Bell className={`w-6 h-6 ${unreadCount > 0 ? 'animate-wiggle' : ''}`} />
              {unreadCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center animate-bounce"
                  style={{ backgroundColor: '#ef4444', color: 'white' }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { 
                localStorage.removeItem('mrfrogs_vip_phone'); 
                setCustomerData(null); 
                setPhone(''); 
                setRestaurantId(null); 
                setNotifications([]); 
                setUnreadCount(0); 
              }}
              className="text-white hover:bg-white/20"
            >
              Salir
            </Button>
          </div>
        </div>

        {/* PWA Install Banner */}
        {showInstallBanner && !isAppInstalled && (
          <div 
            className="mx-4 mb-4 p-4 rounded-2xl shadow-lg"
            style={{ 
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-sm">Instala la App</h3>
                <p className="text-white text-opacity-80 text-xs mt-0.5">Accede más rápido a tu tarjeta VIP</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleInstallClick}
                  className="bg-white text-purple-600 font-semibold text-xs px-3 py-2 rounded-lg"
                >
                  Instalar
                </button>
                <button
                  type="button"
                  onClick={() => setShowInstallBanner(false)}
                  className="text-white text-opacity-70 px-2 py-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* iOS Instructions Modal - Simplified for compatibility */}
        {showIOSInstructions && (
          <div 
            className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-end justify-center"
            style={{ animation: 'fadeIn 0.3s ease-out' }}
            onClick={() => setShowIOSInstructions(false)}
          >
            <div 
              className="w-full max-w-md bg-white rounded-t-3xl p-6 pb-10"
              style={{ animation: 'slideUp 0.3s ease-out' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6"></div>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4">
                  <Smartphone className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-800">Instalar en iPhone</h2>
                <p className="text-gray-500 text-sm mt-1">Sigue estos 3 pasos sencillos</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">1</div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">Toca el botón Compartir</p>
                    <p className="text-sm text-gray-500">El cuadrado con flecha arriba</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                    <Share className="w-5 h-5 text-white" />
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-bold">2</div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">Busca "Añadir a inicio"</p>
                    <p className="text-sm text-gray-500">Desliza hacia abajo</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                    <Plus className="w-5 h-5 text-gray-600" />
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">3</div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">Toca "Añadir"</p>
                    <p className="text-sm text-gray-500">¡Listo!</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowIOSInstructions(false)}
                className="w-full mt-6 h-12 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl"
              >
                Entendido
              </button>
            </div>
          </div>
        )}

        {/* VIP Card */}
        <Card 
          className="border-0 shadow-2xl overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
            borderRadius: '20px'
          }}
        >
          <CardContent className="p-6 text-white">
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="text-sm opacity-80">Cliente VIP</p>
                <h2 className="text-2xl font-bold">{customerData.customer.name}</h2>
                <div className="flex items-center mt-1">
                  <Badge 
                    className="text-sm px-3 py-1"
                    style={{ 
                      backgroundColor: customerData.tier.color,
                      color: COLORS.white
                    }}
                  >
                    {getTierIcon(customerData.tier.tier)} {customerData.tier.label}
                  </Badge>
                </div>
              </div>
              
              {/* QR Code */}
              {qrCodeUrl && (
                <div className="bg-white p-2 rounded-lg">
                  <img src={qrCodeUrl} alt="QR Code" className="w-20 h-20" />
                </div>
              )}
            </div>

            {/* Points Display */}
            <div 
              className="rounded-xl p-4 mb-4"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-sm opacity-80">Puntos Disponibles</p>
                  <p className="text-3xl font-bold" style={{ color: COLORS.gold }}>
                    {customerData.points.balance.toLocaleString()}
                  </p>
                  <p className="text-xs opacity-70">puntos</p>
                </div>
                <div className="text-center">
                  <p className="text-sm opacity-80">Valor en $</p>
                  <p className="text-3xl font-bold" style={{ color: COLORS.gold }}>
                    ${customerData.points.cash_value.toFixed(2)}
                  </p>
                  <p className="text-xs opacity-70">MXN</p>
                </div>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div>
                <p className="opacity-70">Visitas</p>
                <p className="font-bold text-lg">{customerData.stats.visit_count}</p>
              </div>
              <div>
                <p className="opacity-70">Total Gastado</p>
                <p className="font-bold text-lg">${customerData.stats.total_spent.toLocaleString()}</p>
              </div>
              <div>
                <p className="opacity-70">Multiplicador</p>
                <p className="font-bold text-lg">{customerData.points.multiplier}x</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center gap-2 px-4 mt-4">
        {[
          { id: 'card', label: 'Mi Tarjeta', icon: CreditCard },
          { id: 'redeem', label: 'Canjear', icon: Gift },
          { id: 'history', label: 'Historial', icon: History }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            onClick={() => {
              setActiveTab(tab.id);
              if (restaurantId) logPortalVisit(restaurantId, tab.id);
            }}
            className={`flex-1 ${activeTab === tab.id ? '' : 'bg-white'}`}
            style={activeTab === tab.id ? { backgroundColor: COLORS.primary } : {}}
          >
            <tab.icon className="w-4 h-4 mr-1" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-4 mt-4 bg-white min-h-[50vh] rounded-t-3xl -mt-2 relative z-10">
        {/* Card Tab */}
        {activeTab === 'card' && (
          <div className="space-y-4">
            {/* Tier Progress */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center">
                  <Award className="w-5 h-5 mr-2" style={{ color: COLORS.gold }} />
                  Tu Nivel VIP
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">{getTierIcon(customerData.tier.tier)}</span>
                  <div className="flex-1 mx-4">
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          backgroundColor: customerData.tier.color,
                          width: `${Math.min((customerData.stats.total_spent / 5000) * 100, 100)}%`
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      ${customerData.stats.total_spent.toLocaleString()} / $5,000 para Diamante
                    </p>
                  </div>
                  <span className="text-2xl">💎</span>
                </div>

                <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: `${COLORS.primary}15` }}>
                  <p className="text-sm font-medium" style={{ color: COLORS.primaryDark }}>
                    🎁 Beneficios de {customerData.tier.label}:
                  </p>
                  <ul className="text-sm text-gray-600 mt-1">
                    <li>• {customerData.tier.discount}% descuento en compras</li>
                    <li>• {customerData.tier.multiplier}x puntos por cada compra</li>
                    {customerData.tier.tier === 'diamante' && <li>• 🎂 Postre gratis en tu cumpleaños</li>}
                    {customerData.tier.tier === 'oro' && <li>• 🍹 Bebida de cortesía mensual</li>}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Member Info */}
            <Card>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Miembro desde</p>
                    <p className="font-medium">{formatDate(customerData.customer.member_since)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Última visita</p>
                    <p className="font-medium">{formatDate(customerData.customer.last_visit)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Ticket promedio</p>
                    <p className="font-medium">${customerData.stats.avg_ticket.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Puntos ganados</p>
                    <p className="font-medium">{customerData.points.earned.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Redeem Tab */}
        {activeTab === 'redeem' && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-gray-600">
                Tienes <span className="font-bold" style={{ color: COLORS.primary }}>
                  {customerData.points.balance.toLocaleString()} puntos
                </span> disponibles
              </p>
            </div>

            {redeemableProducts.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  <Gift className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay productos disponibles para canje en este momento.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {redeemableProducts.map(product => (
                  <Card 
                    key={product.product_id}
                    className={`overflow-hidden ${!product.can_afford ? 'opacity-60' : ''}`}
                  >
                    <CardContent className="p-0">
                      <div className="flex">
                        {/* Product Image */}
                        <div className="w-24 h-24 flex-shrink-0 bg-gray-100">
                          {product.image_url ? (
                            <img 
                              src={getImageUrl(product.image_url)} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // If image fails to load, show placeholder
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-3xl" style="background-color: rgba(16, 185, 129, 0.15);">🍽️</div>';
                              }}
                            />
                          ) : (
                            <div 
                              className="w-full h-full flex items-center justify-center text-3xl"
                              style={{ backgroundColor: `${COLORS.primary}15` }}
                            >
                              🍽️
                            </div>
                          )}
                        </div>
                        {/* Product Info */}
                        <div className="flex-1 p-3 flex flex-col justify-between">
                          <div>
                            <h3 className="font-semibold text-sm leading-tight">{product.name}</h3>
                            <p className="text-xs text-gray-500">{product.category}</p>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                              <Badge 
                                className="text-xs px-2 py-0.5"
                                style={{ backgroundColor: COLORS.gold, color: 'white' }}
                              >
                                {product.points_cost.toLocaleString()} pts
                              </Badge>
                              <span className="text-xs text-gray-400">
                                ${product.regular_price}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              disabled={!product.can_afford || loading}
                              onClick={() => requestRedemption(product.product_id)}
                              className="text-xs h-7 px-3"
                              style={{ 
                                backgroundColor: product.can_afford ? COLORS.primary : '#ccc',
                                color: COLORS.white
                              }}
                            >
                              {product.can_afford ? 'Canjear' : `Faltan ${product.points_needed}`}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-3">
            {(!customerData.history || customerData.history.length === 0) ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay movimientos en tu historial.</p>
                </CardContent>
              </Card>
            ) : (
              customerData.history.slice().reverse().map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            item.points > 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {item.points > 0 ? <TrendingUp className="w-5 h-5" /> : <Gift className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{item.description}</p>
                          <p className="text-xs text-gray-500">{formatDate(item.date)}</p>
                        </div>
                      </div>
                      <span className={`font-bold ${item.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.points > 0 ? '+' : ''}{item.points.toLocaleString()} pts
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Redemption Modal */}
      {showRedemptionModal && pendingRedemption && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-sm">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center"
                style={{ backgroundColor: `${COLORS.primary}20` }}
              >
                <CheckCircle2 className="w-10 h-10" style={{ color: COLORS.primary }} />
              </div>
              <CardTitle>¡Canje Solicitado!</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <p className="text-gray-600 mb-2">{pendingRedemption.product_name}</p>
                <p className="text-sm text-gray-500">-{pendingRedemption.points_cost} puntos</p>
              </div>

              <div 
                className="p-4 rounded-lg text-center"
                style={{ backgroundColor: `${COLORS.navy}10` }}
              >
                <p className="text-sm text-gray-500 mb-1">Código de Canje</p>
                <p className="text-3xl font-mono font-bold tracking-widest" style={{ color: COLORS.navy }}>
                  {pendingRedemption.code}
                </p>
              </div>

              <div 
                className="p-4 rounded-lg text-center"
                style={{ backgroundColor: `${COLORS.gold}20` }}
              >
                <p className="text-sm text-gray-500 mb-1 flex items-center justify-center">
                  <Lock className="w-4 h-4 mr-1" /> PIN de Seguridad
                </p>
                <p className="text-4xl font-mono font-bold tracking-widest" style={{ color: COLORS.gold }}>
                  {pendingRedemption.pin}
                </p>
              </div>

              <div className="text-center text-sm text-gray-500">
                <Clock className="w-4 h-4 inline mr-1" />
                Válido por 24 horas
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-yellow-800">📱 Instrucciones:</p>
                <ol className="text-yellow-700 mt-1 list-decimal list-inside">
                  <li>Muestra este código al cajero</li>
                  <li>Di tu PIN en voz alta</li>
                  <li>¡Disfruta tu producto!</li>
                </ol>
              </div>

              <Button 
                className="w-full"
                onClick={() => {
                  setShowRedemptionModal(false);
                  setPendingRedemption(null);
                  lookupCustomer(); // Refresh data
                }}
                style={{ backgroundColor: COLORS.primary }}
              >
                Entendido
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bottom Navigation */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex justify-around py-2">
          {[
            { id: 'card', icon: CreditCard, label: 'Tarjeta' },
            { id: 'redeem', icon: Gift, label: 'Canjear' },
            { id: 'history', icon: History, label: 'Historial' }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex flex-col items-center py-2 px-4 rounded-lg transition-colors ${
                activeTab === item.id ? 'text-white' : 'text-gray-500'
              }`}
              style={activeTab === item.id ? { backgroundColor: COLORS.primary } : {}}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs mt-1">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* CSS Animations for Main Portal */}
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }
        .animate-slideInRight { animation: slideInRight 0.4s ease-out forwards; }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .animate-wiggle { animation: wiggle 0.5s ease-in-out infinite; }
      `}</style>
      
      {/* Smart Frog Mascot - Mr. Frogs Intelligent Assistant */}
      {customerData && (
        <SmartFrogMascot 
          customerData={customerData}
          redeemableProducts={redeemableProducts}
          unreadNotifications={unreadCount}
          onProductClick={(product) => {
            setActiveTab('redeem');
            toast.success(`¡Mira ${product.name}! 🐸`);
          }}
        />
      )}
    </div>
  );
};

export default CustomerPortal;
